import { type ReactNode } from 'react'
import { Button } from '@/components/Button'
import { ButtonCollection } from '@/components/ButtonCollection'
import { SideSheet } from '@/components/SideSheet'
import {
  SideSheetHeader,
  SideSheetHeaderAvatar,
  SideSheetHeaderContent,
  SideSheetHeaderDescription,
  SideSheetHeaderText,
  SideSheetHeaderTitle,
} from '@/components/SideSheetHeader'
import { type AvatarSubject } from '@/components/Avatar'
import { useScrollOffsets } from '@/hooks/element-hooks'
import { NoOpportunitySelected } from './NoOpportunitySelected'
import styles from './OpportunityDetailsSheet.module.css'
import { formattedTimeUntil, dateFormatters } from '@/utils/date-util'
import {
  OpportunityDetailsContent,
  OpportunityDetailsContentItem,
  NoInfo,
} from '@/features/opportunities/components/OpportunityDetailsContent'
import { OpportunityDetailsActions } from '@/features/opportunities/components/OpportunityDetailsActions'
import logoWorklayerBlueUrl from '@/assets/misc/logo_worklayer-blue.svg'

export interface OpportunityDetailsSheetHeader {
  workspaceName: string
  workspaceIcon: string
  client?: AvatarSubject
  name: string
  description: string
}

export interface OpportunityDetailsSheetProps {
  header: OpportunityDetailsSheetHeader | null
  onClose?: () => void
  actions?: ReactNode
  children?: ReactNode
  corporateMode: boolean
}

export function OpportunityDetailsSheet({
  header,
  onClose,
  actions,
  children,
  corporateMode,
}: OpportunityDetailsSheetProps) {
  const { connectRef: rootRef, scrollYStart: scrollOffset } = useScrollOffsets()
  return (
    <SideSheet>
      <div className={styles.root} ref={rootRef}>
        {!header ? (
          <NoOpportunitySelected corporateMode={corporateMode} />
        ) : (
          <>
            <SideSheetHeader
              bordered
              onClose={onClose}
              scrollOffset={scrollOffset}
            >
              <SideSheetHeaderContent>
                <SideSheetHeaderAvatar
                  cornerSubject={{
                    displayName: header.workspaceName,
                    avatar: header.workspaceIcon || logoWorklayerBlueUrl,
                  }}
                  subject={header.client}
                />
                <SideSheetHeaderText>
                  <SideSheetHeaderTitle>{header.name}</SideSheetHeaderTitle>
                  <SideSheetHeaderDescription>
                    {header.description || 'No description'}
                  </SideSheetHeaderDescription>
                </SideSheetHeaderText>
                {actions}
              </SideSheetHeaderContent>
            </SideSheetHeader>
            {children}
          </>
        )}
      </div>
    </SideSheet>
  )
}

export interface OpportunityDetailsPickupActionsProps {
  showPickup: boolean
  isPickupPending: boolean
  onPickup: () => unknown
  showApply: boolean
  applied: boolean
  isApplyPending: boolean
  onApply: () => unknown
  onWithdraw: () => unknown
  timeUntilSelection: number | null
}

export function OpportunityDetailsPickupActions({
  showPickup,
  isPickupPending,
  onPickup,
  showApply,
  applied,
  isApplyPending,
  onApply,
  onWithdraw,
  timeUntilSelection,
}: OpportunityDetailsPickupActionsProps) {
  return (
    <OpportunityDetailsActions>
      <ButtonCollection fullWidth>
        {showPickup && (
          <Button
            label="Pickup Job"
            variant="primary"
            isPending={isPickupPending}
            isDisabled={isPickupPending}
            onPress={onPickup}
            testId="pickup-job-button"
            block
          />
        )}
        {showApply && (
          <>
            <Button
              label={!applied ? "I'm interested" : "I'm no longer interested"}
              variant={applied ? 'destructive' : 'primary'}
              isPending={isApplyPending}
              isDisabled={isApplyPending}
              onPress={applied ? onWithdraw : onApply}
              testId={`job-${!applied ? 'apply' : 'widthdraw'}-button`}
              block
            />
            {!!timeUntilSelection && (
              <p className={styles.selectionCountdown}>
                <span className={styles.text}>Selecting in</span>{' '}
                <span
                  className={
                    applied ? styles.countdownApplied : styles.countdown
                  }
                >
                  {formattedTimeUntil(timeUntilSelection)}
                </span>
              </p>
            )}
          </>
        )}
      </ButtonCollection>
    </OpportunityDetailsActions>
  )
}

export interface OpportunityDetailsBodyProps {
  infoLines: { title: string; content: string }[]
  providerCutAmount: string | null
  deadline: Date | null
  scopeItems: { name: string; quantity: number }[] | null
}

export function OpportunityDetailsBody({
  infoLines,
  providerCutAmount,
  deadline,
  scopeItems,
}: OpportunityDetailsBodyProps) {
  const hasChildren = infoLines.length || providerCutAmount !== null || deadline

  const children = (
    <>
      {infoLines.map((infoLine, idx) => (
        <OpportunityDetailsContentItem title={infoLine.title} key={idx}>
          {infoLine.content}
        </OpportunityDetailsContentItem>
      ))}
      {providerCutAmount !== null && (
        <OpportunityDetailsContentItem title="Total">
          {providerCutAmount}
        </OpportunityDetailsContentItem>
      )}
      {deadline && (
        <OpportunityDetailsContentItem title="Deadline" icon="clock">
          {dateFormatters.dateTimeDefault.format(deadline)}
        </OpportunityDetailsContentItem>
      )}
      {scopeItems && (
        <OpportunityDetailsContentItem title="Service items">
          {scopeItems.map((i) => (
            <div key={i.name} className={styles.scopeItem}>
              <div>{i.name}</div>
              <div className={styles.scopeItemCount}>{i.quantity}</div>
            </div>
          ))}
        </OpportunityDetailsContentItem>
      )}
    </>
  )

  return (
    <OpportunityDetailsContent>
      {hasChildren ? (
        children
      ) : (
        <NoInfo>
          <p>
            This opportunity is scarce on the details.
            <br />
            In other words, there's nothing to display here.
          </p>
        </NoInfo>
      )}
    </OpportunityDetailsContent>
  )
}
